import { useSelector } from 'react-redux'
import { Redirect, Route, type RouteProps, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { useModuleConfig } from '@webapp/app/config/useModuleConfig'
import { uuidRegExp } from '@webapp/config/react-router'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { PayCardStatuses } from '@webapp/models/enums'
import {
  CardholderAccountEditPage,
  CardholderAccountPage,
  CardholderAccountSetupPage,
  CardholderAccountVerifyPage,
  CardManagementPage,
  CardManagementPageTabStatuses,
  RequestVirtualCardPage,
  TransactionReconciliationPage,
  TransactionReconciliationPageTabStatuses,
} from '@webapp/modules/spending-cards'
import {
  SpendingCardCreateFundRequestPage,
  SpendingCardRequestVirtualCardPage,
  SpendingCardTransactionListPage,
  SpendingCardVirtualCardRequestDetailsPage,
  SpendingCardVirtualCardTopUpDetailsPage,
} from '@webapp/pages'
import {
  SpendingCardApprovalListPage,
  SpendingCardApprovalListPageTabs,
} from '@webapp/pages/SpendingCardApprovalListPage'
import { SpendingCardApproveFundRequestPage } from '@webapp/pages/SpendingCardApproveFundRequestPage'
import { SpendingCardApproveVirtualCardRequestPage } from '@webapp/pages/SpendingCardApproveVirtualCardRequestPage'
import { SpendingCardFundDetailsPage } from '@webapp/pages/SpendingCardFundDetailsPage'
import { SpendingCardFundRequestDetailsPage } from '@webapp/pages/SpendingCardFundRequestDetailsPage'
import { SpendingCardManagementDetailsPage } from '@webapp/pages/SpendingCardManagementDetailsPage'
import { SpendingCardManagementFundDetailsPage } from '@webapp/pages/SpendingCardManagementFundDetailsPage'
import {
  SpendingCardPhysicalCardPage,
  SpendingCardPhysicalCardTabs,
} from '@webapp/pages/SpendingCardPhysicalCardPage'
import { SpendingCardTransactionListPageTabs } from '@webapp/pages/SpendingCardTransactionListPage'
import { SpendingCardVirtualCardDetailsPage } from '@webapp/pages/SpendingCardVirtualCardDetailsPage'
import {
  SpendingCardVirtualCardsPage,
  SpendingCardVirtualCardsPageTabs,
} from '@webapp/pages/SpendingCardVirtualCardsPage'

const RequestFundRoute = ({ path, component }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      navigationPermissions={['request__pay_request']}
      customFilter={({ featureSwitches, navigationPermissions }) =>
        navigationPermissions.includes('request__pay_request') &&
        featureSwitches.enable_airwallex_issuing
      }
    />
  )
}

const ApproveRequestRoute = ({ path, component }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      navigationPermissions={['approval__pay_request']}
      customFilter={({ featureSwitches, navigationPermissions }) =>
        navigationPermissions.includes('approval__pay_request') &&
        featureSwitches.enable_airwallex_issuing
      }
    />
  )
}

const VirtualCardsRoute = ({ path, component, exact = false }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      customFilter={({ featureSwitches, navigationPermissions }) =>
        navigationPermissions.includes('pay__virtual_cards') &&
        featureSwitches.enable_airwallex_issuing
      }
      exact={exact}
    />
  )
}

const PhysicalCardRoute = ({ path, component }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      customFilter={({ featureSwitches, navigationPermissions }) =>
        navigationPermissions.includes('pay__my_card') &&
        featureSwitches.enable_airwallex_issuing
      }
    />
  )
}

const CardManagementRoute = ({ path, component: Component }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      render={(props) => <Component {...props} />}
      customFilter={({ featureSwitches, navigationPermissions }) =>
        navigationPermissions.includes('pay__card_management') &&
        featureSwitches.enable_airwallex_issuing
      }
    />
  )
}

const ReconcileTransactionsRoute = ({ path, component }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      navigationPermissions={['pay__reconcile_transactions']}
      customFilter={({ featureSwitches, navigationPermissions }) =>
        navigationPermissions.includes('pay__reconcile_transactions') &&
        featureSwitches.enable_airwallex_issuing
      }
    />
  )
}

const CardholderRoute = ({ path, component, exact = false }: RouteProps) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      customFilter={({
        featureSwitches,
        navigationPermissions,
        setupProgress,
      }) =>
        navigationPermissions.includes('pay__cardholder_account') &&
        featureSwitches.enable_airwallex_issuing &&
        setupProgress.is_financial_account_setup === true
      }
      exact={exact}
    />
  )
}

export const SpendingCardRouter = () => {
  const { url, brp } = useAppRoutes()

  const {
    routes: { enableSpendingCardRoutes },
  } = useModuleConfig()

  const { enable_airwallex_issuing } = useSelector(makeSelectFeatureSwitches())

  if (!enableSpendingCardRoutes) return <Redirect to={url('DashboardPage')} />

  return (
    <Switch>
      {/* RFF Routes */}
      <RequestFundRoute
        path={`/spending-card/requests/:uuid(${uuidRegExp.source})`}
        component={SpendingCardFundRequestDetailsPage}
      />
      <RequestFundRoute
        path={brp('SpendingCardPhysicalCardPage', {
          tab: `:tab(${SpendingCardPhysicalCardTabs.PENDING}|${SpendingCardPhysicalCardTabs.OPEN}|${SpendingCardPhysicalCardTabs.CLOSED})`,
        })}
        component={SpendingCardPhysicalCardPage}
      />
      <RequestFundRoute
        path={brp('SpendingCardCreateFundRequestPage')}
        component={SpendingCardCreateFundRequestPage}
      />
      <Redirect
        from='/spending-card/requests/create'
        to={url('SpendingCardCreateFundRequestPage')}
      />
      <Redirect
        from='/spending-card/requests/create'
        to={url('SpendingCardPhysicalCardPage', {
          tab: SpendingCardPhysicalCardTabs.PENDING,
        })}
      />
      <Redirect
        path='/spending-card/requests/create'
        to={url('SpendingCardPhysicalCardPage', {
          tab: SpendingCardPhysicalCardTabs.PENDING,
        })}
      />
      {/* Approve Fund Requests Routes */}
      <ApproveRequestRoute
        path={brp('SpendingCardApprovalListPage', {
          tab: `:tab(${SpendingCardApprovalListPageTabs.ALL}|${SpendingCardApprovalListPageTabs.APPROVED}|${SpendingCardApprovalListPageTabs.DENIED}|${SpendingCardApprovalListPageTabs.PENDING})`,
        })}
        component={SpendingCardApprovalListPage}
      />
      <ApproveRequestRoute
        path={url('SpendingCardApproveFundRequestPage', {
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={SpendingCardApproveFundRequestPage}
      />
      <ApproveRequestRoute
        path={url('SpendingCardApproveVirtualCardRequestPage', {
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={SpendingCardApproveVirtualCardRequestPage}
      />
      <Redirect
        from={brp('SpendingCardApprovalListPage', {
          tab: '',
        })}
        to={url('SpendingCardApprovalListPage', {
          tab: SpendingCardApprovalListPageTabs.PENDING,
        })}
      />
      {/* Virtual Cards Routes */}
      <VirtualCardsRoute
        path={brp('SpendingCardVirtualCardsPage', {
          tab: `:tab(${SpendingCardVirtualCardsPageTabs.ACTIVE}|${SpendingCardVirtualCardsPageTabs.BLOCKED}|${SpendingCardVirtualCardsPageTabs.DENIED}|${SpendingCardVirtualCardsPageTabs.PENDING}|${SpendingCardVirtualCardsPageTabs.LOCKED})`,
        })}
        component={SpendingCardVirtualCardsPage}
      />
      <VirtualCardsRoute
        path={brp('SpendingCardRequestVirtualCardPage')}
        component={SpendingCardRequestVirtualCardPage}
        exact
      />
      <VirtualCardsRoute
        path={brp('RequestVirtualCardPage')}
        component={RequestVirtualCardPage}
        exact
      />
      <VirtualCardsRoute
        path={brp('IssuingRequestVirtualCardPage')}
        component={RequestVirtualCardPage}
        exact
      />
      <VirtualCardsRoute
        path={brp('SpendingCardVirtualCardDetailsPage', {
          uuid: `:uuid`,
        })}
        component={SpendingCardVirtualCardDetailsPage}
        exact
      />
      <VirtualCardsRoute
        path={brp('SpendingCardVirtualCardRequestDetailsPage', {
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={SpendingCardVirtualCardRequestDetailsPage}
      />
      <VirtualCardsRoute
        path={brp('SpendingCardVirtualCardTopUpDetailsPage', {
          card: `:card`,
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={SpendingCardVirtualCardTopUpDetailsPage}
        exact
      />
      <Redirect
        from={brp('SpendingCardVirtualCardsPage', {
          tab: '',
        })}
        to={url('SpendingCardVirtualCardsPage', {
          tab: SpendingCardVirtualCardsPageTabs.ACTIVE,
        })}
      />
      {/* Physical Card Routes */}
      <PhysicalCardRoute
        path={brp('SpendingCardPhysicalCardPage', {
          tab: `:tab(${SpendingCardPhysicalCardTabs.PENDING}|${SpendingCardPhysicalCardTabs.OPEN}|${SpendingCardPhysicalCardTabs.CLOSED})`,
        })}
        component={SpendingCardPhysicalCardPage}
      />
      <PhysicalCardRoute
        path={brp('SpendingCardFundRequestDetailsPage', {
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={SpendingCardFundRequestDetailsPage}
      />
      <PhysicalCardRoute
        path={brp('SpendingCardFundDetailsPage', {
          uuid: `:uuid(${uuidRegExp.source})`,
        })}
        component={SpendingCardFundDetailsPage}
      />
      <PhysicalCardRoute
        path={brp('SpendingCardCreateFundRequestPage')}
        component={SpendingCardCreateFundRequestPage}
      />
      <Redirect
        from={brp('SpendingCardPhysicalCardPage', {
          tab: '',
        })}
        to={url('SpendingCardPhysicalCardPage', {
          tab: SpendingCardPhysicalCardTabs.PENDING,
        })}
      />
      {/* My Transactions Route */}
      <ProtectedRoute
        path={brp('SpendingCardTransactionListPage', {
          tab: `:tab(${SpendingCardTransactionListPageTabs.SUCCESSFUL}|${SpendingCardTransactionListPageTabs.DECLINED})`,
        })}
        component={SpendingCardTransactionListPage}
        navigationPermissions={['pay__my_transactions']}
        customFilter={({ featureSwitches, navigationPermissions }) =>
          navigationPermissions.includes('pay__my_transactions') &&
          featureSwitches.enable_airwallex_issuing
        }
        exact
      />
      <Redirect
        from={brp('SpendingCardTransactionListPage', {
          tab: '',
        })}
        to={url('SpendingCardTransactionListPage', {
          tab: SpendingCardTransactionListPageTabs.SUCCESSFUL,
        })}
      />
      {/* Card Management Routes */}
      <CardManagementRoute
        path={brp('CardManagementPage', {
          status: `:status(${CardManagementPageTabStatuses.ACTIVE}|${CardManagementPageTabStatuses.BLOCKED}|${CardManagementPageTabStatuses.CANCELED}|${CardManagementPageTabStatuses.LOCKED}|${CardManagementPageTabStatuses.PENDING})`,
        })}
        component={CardManagementPage}
      />
      <CardManagementRoute
        path={brp('CardManagementDetailsPage', {
          id: ':id',
          fundStatus: ':fundStatus',
        })}
        component={SpendingCardManagementDetailsPage}
      />
      <CardManagementRoute
        path={brp('CardManagementFundDetailsPage', {
          id: ':id',
          fundId: ':fundId',
        })}
        component={SpendingCardManagementFundDetailsPage}
      />

      <Redirect
        path={'/spending-card/manage-cards'}
        to={brp('CardManagementPage', { status: PayCardStatuses.ACTIVE })}
      />
      {/* Reconcile Transactions Routes */}
      <ReconcileTransactionsRoute
        path={brp('TransactionReconciliationPage', {
          tab: `:tab(${TransactionReconciliationPageTabStatuses.NEEDS_REVIEW}|${TransactionReconciliationPageTabStatuses.REVIEWED})`,
        })}
        component={TransactionReconciliationPage}
      />
      {enable_airwallex_issuing && (
        <Redirect
          exact
          path='/spending-card/reconciliation'
          to={url('TransactionReconciliationPage', {
            tab: TransactionReconciliationPageTabStatuses.NEEDS_REVIEW,
          })}
        />
      )}

      {/* Cardholder Routes */}
      <CardholderRoute
        path={brp('CardholderAccountPage')}
        component={CardholderAccountPage}
        exact
      />
      <CardholderRoute
        path={brp('CardholderAccountSetupPage')}
        component={CardholderAccountSetupPage}
        exact
      />
      <CardholderRoute
        path={brp('CardholderAccountEditPage')}
        component={CardholderAccountEditPage}
        exact
      />
      <CardholderRoute
        path={brp('CardholderAccountVerifyPage')}
        component={CardholderAccountVerifyPage}
        exact
      />
      <Route path='*' render={() => <Redirect to='/' />} />
    </Switch>
  )
}
